/**
 * Tool Parsing Utilities
 * Functions for parsing tool call markers and tool button markers from AI responses
 */

const TOOL_CALL_REGEX_SOURCE = "\\[\\[tool-call:([\\w-]+)(?:\\|([^\\]]+))?\\]\\]";
const TOOL_BUTTON_REGEX_SOURCE = "\\[\\[tool:([\\w-]+)(?:\\|([^\\]]+))?\\]\\]";

export const createToolCallRegex = () => new RegExp(TOOL_CALL_REGEX_SOURCE, "g");
export const createToolButtonRegex = () => new RegExp(TOOL_BUTTON_REGEX_SOURCE, "g");

export const unwrapTemplatePayload = (value) => {
        if (typeof value !== "string") {
                return "";
        }

        let result = value.trim();

        while (result.startsWith("${") && result.endsWith("}")) {
                result = result.slice(2, -1).trim();
        }

        if (
                (result.startsWith("\"") && result.endsWith("\"")) ||
                (result.startsWith("'") && result.endsWith("'"))
        ) {
                result = result.slice(1, -1).trim();
        }

        return result;
};

export const parseToolMarkerValue = (rawValue) => {
        const initial = typeof rawValue === "string" ? rawValue : "";
        const cleaned = unwrapTemplatePayload(initial);

        if (!cleaned) {
                return { label: null, payload: null };
        }

        try {
                const parsed = JSON.parse(cleaned);

                if (parsed === null || parsed === undefined) {
                        return { label: null, payload: null };
                }

                if (typeof parsed === "string" || typeof parsed === "number" || typeof parsed === "boolean") {
                        return { label: String(parsed), payload: null };
                }

                if (typeof parsed === "object" && !Array.isArray(parsed)) {
                        const payloadSource =
                                parsed && parsed.payload && typeof parsed.payload === "object" && !Array.isArray(parsed.payload)
                                        ? parsed.payload
                                        : parsed;

                        let label = null;

                        if (typeof parsed.label === "string" && parsed.label.trim()) {
                                label = parsed.label.trim();
                        } else if (typeof payloadSource.label === "string" && payloadSource.label.trim()) {
                                label = payloadSource.label.trim();
                        }

                        const payloadEntries = Object.entries(payloadSource);
                        const payload = payloadEntries.reduce((acc, [key, value]) => {
                                acc[key] = value;
                                return acc;
                        }, {});

                        return {
                                label,
                                payload: payloadEntries.length > 0 ? payload : null,
                        };
                }
        } catch (error) {
                // Fall back to using the cleaned value as label text
        }

        return { label: cleaned, payload: null };
};

/**
 * Extract tool calls from text
 * @param {string} text - Text containing tool call markers
 * @returns {Array<{id: string, payload: any}>} Array of tool call objects
 */
export const extractToolCalls = (text) => {
        const regex = createToolCallRegex();
        const matches = [];
        let match;

        while ((match = regex.exec(text || "")) !== null) {
                let payload = null;

                if (match[2]) {
                        try {
                                payload = JSON.parse(match[2]);
                        } catch (parseError) {
                                console.warn(
                                        "[toolParsing][extractToolCalls] - Failed to parse tool payload:",
                                        match[2],
                                        parseError
                                );
                                payload = match[2];
                        }
                }

                matches.push({
                        id: match[1],
                        payload,
                });
        }

        return matches;
};

